/**
 * API Service pro komunikaci s Worker backendem
 * Používá localStorage pro session token (cross-domain řešení)
 */

const API_BASE = import.meta.env.VITE_API_URL || '';
const TOKEN_KEY = 'ga_session_token';

class ApiService {
  /**
   * Get token from localStorage
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Set token to localStorage
   */
  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Clear token from localStorage
   */
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
  }

  /**
   * Make authenticated request with automatic token refresh
   */
  async authenticatedFetch(url, options = {}) {
    const token = this.getToken();

    const headers = {
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers
    });

    // Check for new token in response (after token refresh)
    const newToken = response.headers.get('X-New-Token');
    if (newToken) {
      this.setToken(newToken);
    }

    return response;
  }

  /**
   * Check authentication status
   */
  async checkStatus() {
    const response = await this.authenticatedFetch(`${API_BASE}/api/status`);

    if (!response.ok) {
      throw new Error('Failed to check status');
    }

    return await response.json();
  }

  /**
   * Initiate Google OAuth login
   */
  loginWithGoogle() {
    window.location.href = `${API_BASE}/auth/google`;
  }

  /**
   * Logout
   */
  async logout() {
    const response = await this.authenticatedFetch(`${API_BASE}/auth/logout`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }

    // Clear localStorage token
    this.clearToken();

    return await response.json();
  }

  /**
   * Get GA4 properties
   */
  async getProperties() {
    const response = await this.authenticatedFetch(`${API_BASE}/api/properties`);

    if (!response.ok) {
      throw new Error('Failed to fetch properties');
    }

    return await response.json();
  }

  /**
   * Send chat query (V2.0 - with context)
   */
  async sendQuery(query, propertyId, context = []) {
    const response = await this.authenticatedFetch(`${API_BASE}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query, propertyId, context })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to process query');
    }

    return await response.json();
  }

  /**
   * Get user preferences
   */
  async getPreferences() {
    const response = await this.authenticatedFetch(`${API_BASE}/api/preferences`);

    if (!response.ok) {
      throw new Error('Failed to fetch preferences');
    }

    return await response.json();
  }

  /**
   * Get suggested queries
   */
  async getSuggestedQueries() {
    const response = await this.authenticatedFetch(`${API_BASE}/api/suggested-queries`);

    if (!response.ok) {
      throw new Error('Failed to fetch suggested queries');
    }

    return await response.json();
  }

  /**
   * Get cache statistics (V2.0)
   */
  async getCacheStats() {
    const response = await this.authenticatedFetch(`${API_BASE}/api/cache/stats`);

    if (!response.ok) {
      throw new Error('Failed to fetch cache stats');
    }

    return await response.json();
  }

  /**
   * Invalidate cache for property (V2.0)
   */
  async invalidateCache(propertyId) {
    const response = await this.authenticatedFetch(`${API_BASE}/api/cache/invalidate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ propertyId })
    });

    if (!response.ok) {
      throw new Error('Failed to invalidate cache');
    }

    return await response.json();
  }
}

export default new ApiService();
